<template>
  <!--
    Teleport to body so we escape any ancestor stacking context.
    Without this, AssetModal (z-index base+80) is trapped inside the
    FormView stacking context (z-index: 1 as of the parallax fix) while
    the LookFeelSection hub modals teleport to body at z-index base+30 —
    so the teleported parents ended up above the nested AssetModal even
    though AssetModal has the higher z-index value.
  -->
  <teleport to="body">
    <div v-if="show" class="modal-overlay" @click.self="close">
      <div class="modal-container">
        <div class="modal-header">
          <button
          type="button"
          class="modal-close-button"
          @click="close"
          aria-label="Close asset browser"
        >
          &times;
        </button>
        <h3>{{ modalTitle }}</h3>
      </div>
      <div class="modal-body">
        <p class="instructions">
          {{ modalDescription }}
        </p>
        <div v-if="hasMultipleTabs" class="tabs">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            :class="{ active: activeTab === tab.id }"
            @click="activeTab = tab.id"
          >
            {{ tab.name }}
          </button>
        </div>
        <div class="tab-content">
          <div
            class="asset-grid-wrapper"
            :class="`asset-grid-wrapper--${activeTab}`"
            @touchstart="handleTouchStart"
            @touchend="handleTouchEnd"
          >
            <button
              v-if="totalPages > 1"
              type="button"
              class="pagination-button pagination-button--prev"
              :disabled="!canGoPrev"
              @click="goPrevPage"
              aria-label="View previous assets"
            >
              ‹
            </button>
            <div class="asset-grid">
              <div
                v-for="slot in displayedSlots"
                :key="slot.key"
                class="asset-card"
                :class="{
                  selected: slot.type === 'asset' && activeSelectedAssetId === slot.asset.id,
                  'coming-soon': slot.type === 'placeholder'
                }"
                @click="slot.type === 'asset' && handleAssetSelect(slot.asset)"
              >
                <template v-if="slot.type === 'asset'">
                  <img
                    v-if="slot.asset.thumbnailUrl && slot.asset.thumbnailUrl !== 'placeholder-3d'"
                    :src="slot.asset.thumbnailUrl"
                    :alt="slot.asset.name"
                    class="thumbnail"
                  >
                  <div v-else class="thumbnail-placeholder">Default Asset</div>
                  <div class="asset-name">{{ slot.asset.name }}</div>
                </template>
                <template v-else>
                  <div class="thumbnail-placeholder coming-soon-placeholder">
                    <span class="coming-soon-icon">🔒</span>
                  </div>
                  <div class="asset-name">{{ activePlaceholderCardName }}</div>
                  <div class="coming-soon-badge">Coming Soon</div>
                </template>
              </div>
            </div>
            <button
              v-if="totalPages > 1"
              type="button"
              class="pagination-button pagination-button--next"
              :disabled="!canGoNext"
              @click="goNextPage"
              aria-label="View more assets"
            >
              ›
            </button>
          </div>
          <div v-if="totalPages > 1" class="pagination-dots" aria-hidden="true">
            <span
              v-for="pageIndex in totalPages"
              :key="`page-${pageIndex}`"
              class="pagination-dot"
              :class="{ active: currentPage === pageIndex - 1 }"
            />
          </div>
        </div>
      </div>
      <div class="modal-footer">
          <button @click="close" class="done-button">Done</button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import type { PropType } from 'vue';
import type { DefaultAsset } from '@/config/defaultAssets';

export default defineComponent({
  name: 'AssetModal',
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    defaultLogos: {
      type: Array as PropType<DefaultAsset[]>,
      required: true,
    },
    defaultMapMarkers: {
      type: Array as PropType<DefaultAsset[]>,
      required: true,
    },
    defaultBackgrounds: {
      type: Array as PropType<DefaultAsset[]>,
      required: true,
    },
    defaultArAssets: {
      type: Array as PropType<DefaultAsset[]>,
      required: true,
    },
    selectedLogoId: {
      type: String,
      default: undefined,
    },
    selectedMarkerId: {
      type: String,
      default: undefined,
    },
    selectedBackgroundId: {
      type: String,
      default: undefined,
    },
    selectedArAssetId: {
      type: String,
      default: undefined,
    },
    allowedTabs: {
      type: Array as PropType<Array<'logos' | 'markers' | 'backgrounds' | 'models'>>,
      default: () => ['logos', 'markers', 'backgrounds', 'models'],
    },
    initialTab: {
      type: String as PropType<'logos' | 'markers' | 'backgrounds' | 'models' | undefined>,
      default: undefined,
    },
  },
  emits: ['close', 'select-asset'],
  setup(props, { emit }) {
    type TabId = 'logos' | 'markers' | 'backgrounds' | 'models'

    const allTabs: Array<{ id: TabId; name: string }> = [
      { id: 'logos', name: 'Logos' },
      { id: 'markers', name: 'Map Markers' },
      { id: 'backgrounds', name: 'Backgrounds' },
      { id: 'models', name: '3D Models' },
    ];

    const tabs = computed(() => {
      if (!props.allowedTabs || props.allowedTabs.length === 0) {
        return allTabs;
      }

      const allowedSet = new Set<TabId>(props.allowedTabs as TabId[]);
      const filtered = allTabs.filter((tab) => allowedSet.has(tab.id));
      return filtered.length > 0 ? filtered : allTabs;
    });

    const resolveInitialTab = (): TabId => {
      const preferred = props.initialTab as TabId | undefined;
      if (preferred && tabs.value.some((tab) => tab.id === preferred)) {
        return preferred;
      }
      return tabs.value[0]?.id ?? 'logos';
    };

    const activeTab = ref<TabId>(resolveInitialTab());
    const currentPage = ref(0);
    const ITEMS_PER_PAGE = 6;

    const getPlaceholderCardName = (tab: TabId): string => {
      if (tab === 'models') return 'More AR Objects';
      if (tab === 'markers') return 'More map markers';
      if (tab === 'backgrounds') return 'More backgrounds';
      return 'More logos';
    };

    const activePlaceholderCardName = computed(() => getPlaceholderCardName(activeTab.value));

    const assetsByTab = computed(() => ({
      logos: props.defaultLogos ?? [],
      markers: props.defaultMapMarkers ?? [],
      backgrounds: props.defaultBackgrounds ?? [],
      models: props.defaultArAssets ?? [],
    }));

    const activeAssets = computed(() => assetsByTab.value[activeTab.value] ?? []);

    const totalPages = computed(() => {
      const count = activeAssets.value.length;
      return Math.max(1, Math.ceil(count / ITEMS_PER_PAGE));
    });

    const activeSelectedAssetId = computed(() => {
      if (activeTab.value === 'logos') {
        return props.selectedLogoId;
      }
      if (activeTab.value === 'markers') {
        return props.selectedMarkerId;
      }
      if (activeTab.value === 'backgrounds') {
        return props.selectedBackgroundId;
      }
      if (activeTab.value === 'models') {
        return props.selectedArAssetId;
      }
      return undefined;
    });

    type DisplaySlot =
      | { type: 'asset'; asset: DefaultAsset; key: string }
      | { type: 'placeholder'; name: string; key: string };

    const displayedSlots = computed<DisplaySlot[]>(() => {
      const startIndex = currentPage.value * ITEMS_PER_PAGE;
      const assetsForPage = activeAssets.value.slice(startIndex, startIndex + ITEMS_PER_PAGE);
      const placeholdersNeeded = ITEMS_PER_PAGE - assetsForPage.length;

      const placeholderItems: DisplaySlot[] = Array.from({ length: placeholdersNeeded }, (_, index) => {
        const name = getPlaceholderCardName(activeTab.value);
        return {
          type: 'placeholder',
          name,
          key: `placeholder-${activeTab.value}-${currentPage.value}-${index}`,
        };
      });

      return [
        ...assetsForPage.map<DisplaySlot>((asset) => ({
          type: 'asset',
          asset,
          key: asset.id,
        })),
        ...placeholderItems,
      ];
    });

    const canGoPrev = computed(() => totalPages.value > 1 && currentPage.value > 0);
    const canGoNext = computed(() => totalPages.value > 1 && currentPage.value < totalPages.value - 1);

    watch(
      () => props.show,
      (show) => {
        if (show) {
          activeTab.value = resolveInitialTab();
          currentPage.value = 0;
        }
      },
      { immediate: true }
    );

    watch(
      () => props.initialTab,
      () => {
        if (props.show) {
          activeTab.value = resolveInitialTab();
          currentPage.value = 0;
        }
      }
    );

    watch(
      () => props.allowedTabs,
      () => {
        if (props.show) {
          activeTab.value = resolveInitialTab();
          currentPage.value = 0;
        }
      },
      { deep: true }
    );

    watch(activeTab, () => {
      currentPage.value = 0;
    });

    watch(totalPages, (pages) => {
      if (currentPage.value > pages - 1) {
        currentPage.value = pages - 1;
      }
    });

    const goPrevPage = () => {
      if (canGoPrev.value) {
        currentPage.value -= 1;
      }
    };

    const goNextPage = () => {
      if (canGoNext.value) {
        currentPage.value += 1;
      }
    };

    const touchStartX = ref(0);
    const touchEndX = ref(0);

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX.value = e.changedTouches[0].screenX;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      touchEndX.value = e.changedTouches[0].screenX;
      handleSwipe();
    };

    const handleSwipe = () => {
      const swipeThreshold = 50;
      if (touchStartX.value - touchEndX.value > swipeThreshold) {
        goNextPage();
      } else if (touchEndX.value - touchStartX.value > swipeThreshold) {
        goPrevPage();
      }
    };

    const close = () => {
      emit('close');
    };

    const handleAssetSelect = (asset: DefaultAsset) => {
      emit('select-asset', asset);
    };

    const hasMultipleTabs = computed(() => tabs.value.length > 1);

    const modalTitle = computed(() => {
      if (!props.allowedTabs || props.allowedTabs.length === 0 || hasMultipleTabs.value) {
        return 'Browse Default Assets';
      }

      if (props.allowedTabs.includes('logos')) {
        return 'Browse Logos';
      }

      if (props.allowedTabs.includes('markers')) {
        return 'Browse Map Markers';
      }

      if (props.allowedTabs.includes('backgrounds')) {
        return 'Browse Backgrounds';
      }

      if (props.allowedTabs.includes('models')) {
        return 'Browse available 3D AR Objects';
      }

      return 'Browse Default Assets';
    });

    const modalDescription = computed(() => {
      if (!props.allowedTabs || props.allowedTabs.length === 0 || hasMultipleTabs.value) {
        return 'Select default logos, map markers, backgrounds, and 3D models for your campaign. You can mix and match with your own uploaded assets.';
      }

      if (props.allowedTabs.includes('logos')) {
        return '';
      }

      if (props.allowedTabs.includes('markers')) {
        return 'Select a default map marker for your campaign or keep your uploaded asset.';
      }

      if (props.allowedTabs.includes('backgrounds')) {
        return 'Select a default background image for the registration screen or keep your uploaded background.';
      }

      if (props.allowedTabs.includes('models')) {
        return 'Select a default 3D model to power your augmented reality experience.';
      }

      return 'Select an asset from our default library to enhance your campaign.';
    });

    return {
      activeTab,
      currentPage,
      tabs,
      close,
      handleAssetSelect,
      hasMultipleTabs,
      modalTitle,
      modalDescription,
      activePlaceholderCardName,
      displayedSlots,
      activeSelectedAssetId,
      totalPages,
      canGoPrev,
      canGoNext,
      goPrevPage,
      goNextPage,
      handleTouchStart,
      handleTouchEnd,
    };
  },
});
</script>

<style scoped>
.modal-overlay {
  z-index: calc(var(--base-layout-modal-z-index) + 80);
}

.modal-container,
.modal-header,
.modal-body,
.modal-footer {
  background: var(--ui-inverse-surface) !important;
}

.modal-container {
  border: none !important;
  box-shadow: none !important;
  --ui-inverse-text: #ffffff;
  --ui-inverse-text-muted: rgba(255, 255, 255, 0.84);
  --ui-inverse-cta-bg: var(--vt-c-accent);
  --ui-inverse-cta-bg-hover: var(--ui-inverse-cta-bg-hover);
  --ui-inverse-cta-text: var(--color-background);
}

.modal-header,
.modal-footer {
  border: none !important;
}

.modal-container h3,
.modal-container p,
.modal-container label,
.modal-container span,
.modal-container button,
.modal-container li {
  color: #ffffff !important;
}

.modal-overlay .modal-container h3 {
  color: #ffffff !important;
}

.instructions {
  color: #ffffff;
  opacity: 0.9;
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 25px;
  text-align: center;
}

.tabs {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.18);
  margin-bottom: 20px;
}

.tabs button {
  background: none;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 1rem;
  color: #ffffff;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
}

.tabs button.active {
  color: var(--vt-c-accent);
  border-bottom-color: var(--vt-c-accent);
}

.asset-grid-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.pagination-button {
  background: var(--vt-c-accent);
  border: 2px solid var(--vt-c-accent);
  color: var(--color-background);
  font-size: 0;
  cursor: pointer;
  padding: 0 12px;
  border-radius: 9999px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  height: 88px;
  user-select: none;
  line-height: 1;
  position: relative;
}

.pagination-button::before {
  position: absolute;
  top: 46%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: var(--color-background);
  font-size: 4rem;
  line-height: 1;
}

.pagination-button--prev::before {
  content: '‹';
}

.pagination-button--next {
  position: relative;
  overflow: visible;
}

.pagination-button--next::before {
  content: '›';
}

.pagination-button--next::after {
  content: 'More\A Logos';
  position: absolute;
  top: calc(100% + 0.35rem);
  left: 50%;
  transform: translateX(-50%);
  color: #ffffff;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1;
  white-space: pre;
  text-align: left;
  padding-left: 1rem;
}

.asset-grid-wrapper--markers .pagination-button--next::after {
  content: 'More\A Map Markers';
}

.asset-grid-wrapper--backgrounds .pagination-button--next::after {
  content: 'More\A Backgrounds';
}

.asset-grid-wrapper--models .pagination-button--next::after {
  content: 'More\A AR\A Objects';
  text-align: left;
  padding-left: 1rem;
}

.pagination-button:hover:not(:disabled) {
  border-color: var(--ui-inverse-cta-bg-hover);
  color: var(--color-background);
  background-color: var(--ui-inverse-cta-bg-hover);
}

.pagination-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  border-color: var(--vt-c-accent);
}

.asset-grid {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(3, minmax(120px, 1fr));
  gap: 15px;
  align-items: stretch;
  min-height: 200px;
}
.asset-card {
  border: 1px solid rgba(142, 246, 198, 0.32);
  border-radius: 0.9rem;
  padding: 10px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  background: var(--ui-inverse-cta-bg);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.03);
}
.asset-card:hover {
  transform: translateY(-2px);
  border-color: var(--ui-inverse-cta-text);
}
.asset-card.selected {
  border-color: var(--ui-inverse-cta-text);
  box-shadow: 0 0 12px rgba(142, 246, 198, 0.35);
}
.asset-card.coming-soon {
  opacity: 0.6;
  cursor: not-allowed;
  position: relative;
}
.asset-card.coming-soon:hover {
  transform: none;
  border-color: rgba(255, 255, 255, 0.18);
}
.thumbnail {
  width: 100%;
  height: 80px;
  object-fit: contain;
  border-radius: 0.6rem;
  margin-bottom: 8px;
  background: rgba(8, 14, 34, 0.48);
}
.pagination-dots {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 16px;
}

.pagination-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.2);
  transition: background-color 0.2s ease;
}

.pagination-dot.active {
  background-color: var(--vt-c-accent);
}
.thumbnail-placeholder {
  width: 100%;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(8, 14, 34, 0.48);
  border-radius: 0.6rem;
  margin-bottom: 8px;
  font-size: 0.8rem;
  color: var(--ui-inverse-cta-text);
  font-weight: 600;
  text-align: center;
  padding: 5px;
}
.coming-soon-placeholder {
  background-color: var(--color-background-mute);
  opacity: 0.5;
}
.coming-soon-icon {
  font-size: 1.5rem;
  opacity: 0.5;
}
.asset-name {
  font-size: 0.8rem;
  text-align: center;
  color: var(--ui-inverse-cta-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  padding: 0 4px;
}
.coming-soon-badge {
  font-size: 0.65rem;
  text-align: center;
  color: var(--ui-inverse-cta-text);
  opacity: 0.75;
  margin-top: 4px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.done-button {
    padding: 12px 24px;
    font-size: 1.1rem;
    font-weight: 600;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    border: 2px solid var(--vt-c-accent);
    background-color: var(--vt-c-accent);
    color: var(--color-background);
    width: 100%;
}

.modal-overlay .modal-container .done-button {
    border: 2px solid var(--vt-c-accent) !important;
    background-color: var(--vt-c-accent) !important;
    color: var(--color-background) !important;
}

.done-button:hover {
    background-color: var(--ui-inverse-cta-bg-hover);
    border-color: var(--ui-inverse-cta-bg-hover);
    color: var(--color-background);
}

.modal-overlay .modal-container .done-button:hover {
    background-color: var(--ui-inverse-cta-bg-hover) !important;
    border-color: var(--ui-inverse-cta-bg-hover) !important;
    color: var(--color-background) !important;
}

@media (max-width: 640px) {
  .asset-grid {
    grid-template-columns: repeat(2, minmax(100px, 1fr));
    gap: 10px;
  }

  .pagination-button {
    font-size: 1.5rem;
    min-width: 36px;
    height: 36px;
    padding: 4px;
  }

  .asset-grid-wrapper {
    gap: 4px;
  }

  .asset-card {
    padding: 8px;
  }

  .asset-name {
    font-size: 0.75rem;
  }

  .thumbnail, .thumbnail-placeholder {
    height: 60px;
  }
}
</style>
